import jalaali from 'jalaali-js'

export type CalendarType = 'jalali' | 'gregorian'

// تبدیل اعداد انگلیسی به فارسی
export const englishToFarsiNumber = (num: string | number): string => {
  const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
  const farsiDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
  
  let str = String(num)
  englishDigits.forEach((digit, index) => {
    str = str.replace(new RegExp(digit, 'g'), farsiDigits[index])
  })
  return str
}

export const gregorianToJalali = (date: Date): { year: number; month: number; day: number } => {
  const { jy, jm, jd } = jalaali.toJalaali(date.getFullYear(), date.getMonth() + 1, date.getDate())
  return { year: jy, month: jm, day: jd }
}

export const jalaliToGregorian = (year: number, month: number, day: number): Date => {
  const { gy, gm, gd } = jalaali.toGregorian(year, month, day)
  return new Date(gy, gm - 1, gd)
}

export const getCurrentJalaliDate = (): { year: number; month: number; day: number } => {
  return gregorianToJalali(new Date())
}

export const getCurrentCalendarDate = (calendar: CalendarType): { year: number; month: number; day: number } => {
  const now = new Date()
  if (calendar === 'gregorian') {
    return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() }
  }
  return gregorianToJalali(now)
}

export const formatDateLocalized = (input: Date | string, calendar: CalendarType, lang: 'fa' | 'en' = 'fa'): string => {
  let date: Date
  
  if (typeof input === 'string') {
    date = new Date(input)
    // بررسی اینکه date معتبر باشد
    if (isNaN(date.getTime())) {
      return 'Invalid Date'
    }
  } else {
    date = input
  }
  
  if (calendar === 'gregorian') {
    // میلادی: نام‌ ماه به صورت متنی
    const gMonth = date.getMonth()
    const monthNames = lang === 'fa' 
      ? ['ژانویه', 'فوریه', 'مارس', 'آوریل', 'می', 'ژوئن', 'ژوئیه', 'اوت', 'سپتامبر', 'اکتبر', 'نوامبر', 'دسامبر']
      : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    return `${date.getDate()} ${monthNames[gMonth]} ${date.getFullYear()}`
  }
  
  // جلالی: نام‌ماه شمسی
  const j = gregorianToJalali(date)
  if (lang === 'fa') {
    return `${j.day} ${getJalaliMonthName(j.month)} ${j.year}`
  }
  // نمایش انگلیسی
  return `${j.day} ${getJalaliMonthEnglish(j.month)} ${j.year}`
}

// نام‌های روز هفته به شمسی
const JALALI_WEEKDAYS = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه']

export const getJalaliWeekdayName = (date: Date): string => {
  // میلادی: 0=Sunday, 1=Monday, ..., 6=Saturday
  // شمسی: 0=Saturday, 1=Sunday, ..., 6=Friday
  // تبدیل: (dayOfWeek + 1) % 7
  const dayOfWeek = date.getDay()
  const jalaliIndex = (dayOfWeek + 1) % 7
  console.log('🔄 getJalaliWeekdayName - dayOfWeek:', dayOfWeek, 'jalaliIndex:', jalaliIndex, 'name:', JALALI_WEEKDAYS[jalaliIndex])
  return JALALI_WEEKDAYS[jalaliIndex]
}

// نام‌های روز هفته به شمسی
export const getJalaliMonthEnglish = (month: number): string => {
  const names = ['Farvardin', 'Ordibehesht', 'Khordad', 'Tir', 'Mordad', 'Shahrivar', 'Mehr', 'Aban', 'Azar', 'Dey', 'Bahman', 'Esfand']
  return names[month - 1] || ''
}

// تابع کمکی برای نمایش هم‌زمان هر دو تقویم (برای کارت‌های تاریخ)
export const formatDualDates = (date: Date | string, lang: 'fa' | 'en' = 'fa'): { jalali: string; gregorian: string } => {
  const d = typeof date === 'string' ? new Date(date) : date
  const j = gregorianToJalali(d)
  
  // تاریخ شمسی
  const jalaliStr = lang === 'fa'
    ? `${j.day} ${getJalaliMonthName(j.month)} ${j.year}`
    : `${j.day} ${getJalaliMonthEnglish(j.month)} ${j.year}`
  
  // تاریخ میلادی
  const gMonth = d.getMonth()
  const monthNames = lang === 'fa'
    ? ['ژانویه', 'فوریه', 'مارس', 'آوریل', 'می', 'ژوئن', 'ژوئیه', 'اوت', 'سپتامبر', 'اکتبر', 'نوامبر', 'دسامبر']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const gregorianStr = `${d.getDate()} ${monthNames[gMonth]} ${d.getFullYear()}`
  
  return { jalali: jalaliStr, gregorian: gregorianStr }
}

export const getJalaliMonthName = (month: number): string => {
  const names = [
    'فروردین',
    'اردیبهشت',
    'خرداد',
    'تیر',
    'مرداد',
    'شهریور',
    'مهر',
    'آبان',
    'آذر',
    'دی',
    'بهمن',
    'اسفند',
  ]
  return names[month - 1] || ''
}

export const getMonthName = (month: number, lang: 'fa' | 'en' = 'fa'): string => {
  if (lang === 'fa') {
    return getJalaliMonthName(month)
  }
  const enNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  return enNames[month - 1] || ''
}

export const getDaysInJalaliMonth = (month: number): number => {
  if (month <= 6) return 31
  if (month <= 11) return 30
  return 29 // Esfand
}
// تبدیل تاریخ شمسی YYYY-MM-DD به میلادی
export const jalaliStringToGregorian = (jalaliString: string): string => {
  if (!jalaliString || jalaliString.length !== 10) return ''
  const [year, month, day] = jalaliString.split('-').map(Number)
  try {
    const { gy, gm, gd } = jalaali.toGregorian(year, month, day)
    return `${gy}-${String(gm).padStart(2, '0')}-${String(gd).padStart(2, '0')}`
  } catch (e) {
    console.error('Error converting Jalali to Gregorian:', e)
    return ''
  }
}

// تبدیل تاریخ میلادی به فرمت شمسی (YYYY/MM/DD)
export const toJalaliDateString = (date: Date | string): string => {
  try {
    const d = typeof date === 'string' ? new Date(date) : date
    console.log('📅 toJalaliDateString input:', d)
    const jalali = gregorianToJalali(d)
    console.log('📅 gregorianToJalali result:', jalali)
    if (!jalali || jalali.year === undefined) {
      console.error('❌ Invalid jalali result:', jalali)
      return 'Invalid Date'
    }
    const result = `${jalali.year}/${String(jalali.month).padStart(2, '0')}/${String(jalali.day).padStart(2, '0')}`
    console.log('📅 toJalaliDateString result:', result)
    return result
  } catch (error) {
    console.error('❌ toJalaliDateString error:', error)
    return 'Error'
  }
}

// تبدیل تاریخ شمسی (YYYY/MM/DD) به میلادی
export const fromJalaliDateString = (jalaliStr: string): Date => {
  const [year, month, day] = jalaliStr.split('/').map(Number)
  return jalaliToGregorian(year, month, day)
}

// دریافت سال و ماه شمسی برای نمایش ماه
export const getJalaliYearMonth = (date: Date): { year: number; month: number } => {
  const jalali = gregorianToJalali(date)
  return { year: jalali.year, month: jalali.month }
}

// دریافت روز شمسی از تاریخ
export const getJalaliDay = (date: Date): number => {
  const jalali = gregorianToJalali(date)
  return jalali.day
}

// محاسبه روزهای ماه شمسی
export const getJalaliMonthDays = (date: Date): (Date | null)[] => {
  const jalali = gregorianToJalali(date)
  const year = jalali.year
  const month = jalali.month
  
  console.log('🗓️ getJalaliMonthDays:', { year, month, jalaliDate: `${year}/${month}/1` })
  
  // تاریخ اول ماه شمسی
  const firstDayGregorian = jalaliToGregorian(year, month, 1)
  const firstDayOfWeek = firstDayGregorian.getDay() // 0=Sunday
  
  console.log('📅 firstDayOfWeek:', firstDayOfWeek, 'Date:', firstDayGregorian)
  
  // تعداد روزهای ماه شمسی
  let daysInMonth = 31
  if (month > 6) {
    daysInMonth = 30
  }
  if (month === 12) {
    // بررسی سال کبیسه شمسی - استفاده از تبدیل جلو و عقب
    const lastDayGregorian = jalaliToGregorian(year, month, 29)
    const nextMonthGregorian = jalaliToGregorian(year, month + 1, 1)
    const diff = Math.ceil((nextMonthGregorian.getTime() - lastDayGregorian.getTime()) / (1000 * 60 * 60 * 24))
    daysInMonth = diff === 2 ? 30 : 29
  }
  
  console.log('📊 daysInMonth:', daysInMonth)
  
  const result: (Date | null)[] = []
  
  // اضافه کردن خانه‌های خالی قبل از اول ماه
  for (let i = 0; i < firstDayOfWeek; i++) {
    result.push(null)
  }
  
  // اضافه کردن روزهای ماه
  for (let day = 1; day <= daysInMonth; day++) {
    const gregorianDate = jalaliToGregorian(year, month, day)
    result.push(gregorianDate)
  }
  
  console.log('🎯 Month days count:', result.filter(d => d !== null).length, 'Total cells:', result.length)
  console.log('🎯 First few days:', result.slice(0, 10).map((d, i) => d ? `${i}: ${d.toISOString().split('T')[0]}` : `${i}: null`))
  
  return result
}